// ════════════════════════════════════════════════════════════════════
// TERROR — M10 · PWA REGISTRATION
// ────────────────────────────────────────────────────────────────────
// Registers public/sw.js, and refuses to in development.
//
// ── WHY THE DEV GUARD IS NOT OPTIONAL ────────────────────────────────
// A service worker on localhost caches the dev server's output and then
// serves it back after a hot reload. The symptom is an edit that "did
// not take" — for a while, then intermittently, then only in one tab.
// caP'n runs `npm start` all session; that failure mode would cost a
// session to diagnose and would look exactly like a bad edit. So the
// worker exists only in a production build, and this file says why in
// the console when it declines, rather than being silently absent.
//
// ── THE ESCAPE HATCH ─────────────────────────────────────────────────
// killServiceWorker() clears every cache and unregisters. It is exposed
// on window as `terrorKillSW()` because the moment you need it is the
// moment you cannot load the code that would call it — a stuck worker
// is fixed from the console or it is not fixed at all.
// ════════════════════════════════════════════════════════════════════

const supported = () => typeof navigator !== "undefined" && "serviceWorker" in navigator;

export function registerServiceWorker() {
  if (!supported()) return;

  if (process.env.NODE_ENV !== "production") {
    // Also clean up after any worker a previous production build left on
    // this origin — a dev server on the same port inherits it.
    navigator.serviceWorker.getRegistrations?.()
      .then((rs) => { if (rs.length) { console.log("[pwa] dev build — removing", rs.length, "leftover worker(s)."); rs.forEach((r) => r.unregister()); } })
      .catch(() => {});
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ""}/sw.js`)
      .then((reg) => console.log("[pwa] worker registered:", reg.scope))
      .catch((e) => console.warn("[pwa] registration failed (non-fatal):", e?.message));
  });
}

export async function killServiceWorker() {
  if (!supported()) return false;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      r.active?.postMessage({ type: "SW_KILL" });
      await r.unregister();
    }
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    console.log("[pwa] worker and caches cleared. Reload once.");
    return true;
  } catch (e) {
    console.warn("[pwa] kill failed:", e?.message);
    return false;
  }
}

if (typeof window !== "undefined") window.terrorKillSW = killServiceWorker;

// ── INSTALL PROMPT ────────────────────────────────────────────────────
// The browser fires beforeinstallprompt ONCE and only when it feels
// like it. Catching it here means a surface can offer "add to home
// screen" at a moment that makes sense instead of whenever Chrome
// decided. Nothing consumes this yet — that is a one-line read when a
// settings screen wants it, and it costs nothing sitting here.
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });
}
export const canInstall = () => !!deferredPrompt;
export async function promptInstall() {
  if (!deferredPrompt) return { ok: false, reason: "no-prompt" };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return { ok: choice?.outcome === "accepted", outcome: choice?.outcome };
}

const pwa = { registerServiceWorker, killServiceWorker, canInstall, promptInstall };
export default pwa;
